<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import BcsTooltip from './BcsTooltip.vue'
import { htmlToPlainText } from '../utils/sanitizeHtml'

const props = defineProps({
  id: {
    type: String,
    default: 'employerSearchInput'
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  items: {
    type: Array,
    required: true
  },
  searchFields: {
    type: Array,
    required: true
  },
  arrayTerm: {
    type: String,
    default: 'name'
  },
  maxSuggestions: {
    type: Number,
    default: 10
  },
  searchButtonText: {
    type: String,
    default: 'Search'
  },
  clearButtonColor: {
    type: String,
    default: 'var(--bs-primary)'
  },
  showResults: {
    type: Boolean,
    default: true
  },
  customClass: {
    type: String,
    default: ''
  },
  truncateContent: {
    type: [Boolean, Number],
    default: false
  },
  tooltipText: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  resultsCountOverride: {
    type: Number,
    default: null
  },
  // Pre-populates the input and runs the search once on mount. Used by pages that
  // arrive with a search term already chosen (e.g. the Transform search-results page,
  // reached from another view's search box). Empty string = no auto-search (default).
  initialTerm: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['search', 'select', 'clear'])

const searchTerm = ref('')
const effectiveSearchTerm = ref('')
const suggestions = ref([])
const showSearchResults = ref(false)
const totalResults = ref(0)
const searchContainer = ref(null)
const isFocused = ref(false)

const displayedTotalResults = computed(() => {
  return typeof props.resultsCountOverride === 'number'
    ? props.resultsCountOverride
    : totalResults.value
})

const handleClickOutside = (event) => {
  if (searchContainer.value && !searchContainer.value.contains(event.target)) {
    suggestions.value = []
    isFocused.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Seed + auto-run when a term is supplied by the parent (items are expected to be
  // ready by mount time). Suggestions are cleared inside performSearch afterwards.
  if (props.initialTerm && props.initialTerm.trim().length) {
    searchTerm.value = props.initialTerm
    performSearch()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const handleFocus = () => {
  isFocused.value = true
}

const handleBlur = (event) => {
  if (searchContainer.value && !searchContainer.value.contains(event.relatedTarget)) {
    isFocused.value = false
  }
}

const stripHtmlAndTruncate = (html, maxLength = 20) => {
  const cleanText = htmlToPlainText(html)
  return cleanText.length <= maxLength
    ? cleanText
    : `${cleanText.substring(0, maxLength)}...`
}

const getDisplayText = (item) => {
  const originalText = item[props.arrayTerm]
  
  if (typeof props.truncateContent === 'number' && props.truncateContent > 0) {
    return stripHtmlAndTruncate(originalText, props.truncateContent)
  } else if (props.truncateContent === true) {
    return stripHtmlAndTruncate(originalText, 20)
  }
  
  return originalText
}

const performSearch = (source) => {
  const searchSource = source === 'suggestion' ? 'suggestion' : 'submit'
  if (props.disabled) return
  searchTerm.value = searchTerm.value.trim()
  if(searchTerm.value.length < 1) return
  if (!searchTerm.value) {
    suggestions.value = []
    showSearchResults.value = false
    effectiveSearchTerm.value = ''
    emit('clear')
    return
  }
  const term = searchTerm.value.toLowerCase()
  
  const results = props.items.filter(item => {
    return props.searchFields.some(field => {
      const fieldValue = String(item[field] || '').toLowerCase() 
      return fieldValue.includes(term)
    })
  })

  totalResults.value = results.length
  showSearchResults.value = true
  effectiveSearchTerm.value = searchTerm.value
  suggestions.value = []
  emit('search', { term: searchTerm.value, results, source: searchSource })
}

const updateSuggestions = () => {
  if (props.disabled || !searchTerm.value) {
    suggestions.value = []
    return
  }

  const term = searchTerm.value.toLowerCase()
  const filteredItems = props.items.filter(item => {
    return props.searchFields.some(field => {
      const fieldValue = String(item[field] || '').toLowerCase()
      return fieldValue.includes(term)
    })
  })

  const uniqueTexts = new Map()
  filteredItems.forEach(item => {
    const displayText = getDisplayText(item)
    if (!uniqueTexts.has(displayText)) {
      uniqueTexts.set(displayText, {
        text: displayText,
        item
      })
    }
  })

  suggestions.value = Array.from(uniqueTexts.values()).slice(0, props.maxSuggestions)
}

const selectSuggestion = (suggestion) => {
  const typedTerm = searchTerm.value.trim()
  const originalText = suggestion.item[props.arrayTerm]
  searchTerm.value = originalText
  effectiveSearchTerm.value = originalText
  performSearch('suggestion')
  emit('select', suggestion.item, typedTerm)
}

const clearSearch = () => {
  searchTerm.value = ''
  effectiveSearchTerm.value = ''
  suggestions.value = []
  showSearchResults.value = false
  emit('clear')
}

watch(() => props.items, () => {
  updateSuggestions()
}, { deep: true })

defineExpose({
  clearSearch
})

</script>

<template>
  <div class="input-group search-group" :class="{ 'is-focused': isFocused && !disabled, 'is-disabled': disabled, [customClass]: true }">
    <div class="search-container" ref="searchContainer" tabindex="0">
      <input
        :id="id"
        type="text"
        v-model="searchTerm"
        @input="updateSuggestions"
        @keyup.enter="performSearch"
        @focus="handleFocus"
        @blur="handleBlur"
        class="form-control search-input"
        :placeholder="placeholder"
        :disabled="disabled"
        aria-label="Search" />
      <button v-if="searchTerm && !disabled" @click="clearSearch" class="clear-button">
        <i class="bi bi-x-circle-fill" :style="{ color: clearButtonColor }"></i>
      </button>
      <ul id="employer-suggestions" v-if="suggestions.length > 0" class="suggestions-list">
        <li
          v-for="(suggestion, index) in suggestions"
          :key="`${suggestion.item.id}-${index}`"
          @click="selectSuggestion(suggestion)">
          {{ suggestion.text }}
        </li>
      </ul>
    </div>
    <template v-if="tooltipText">
      <BcsTooltip :simpleText="tooltipText" :show-arrow="true" :contentClass="'simple-text-tooltip'">
        <button 
          id="employer-search-button"
          type="button" 
          class="btn btn-primary search-button" 
          :disabled="disabled"
          @click="performSearch">
          <i class="bi bi-search"></i>
          {{ searchButtonText }}
        </button>
      </BcsTooltip>
    </template>
    <button
      v-else
      id="employer-search-button"
      type="button" 
      class="btn btn-primary search-button" 
      :disabled="disabled"
      @click="performSearch">
      <i class="bi bi-search"></i>
      {{ searchButtonText }}
    </button>
  </div>

  <div v-if="showSearchResults && showResults" class="results-for-search">
    <h5 class="search-results-text">
      {{ displayedTotalResults }} {{ displayedTotalResults === 1 ? 'Result' : 'Results' }} for
      {{ effectiveSearchTerm }}
    </h5>
    <span @click="clearSearch" class="clear-search-link">Clear Search</span>
  </div>
</template>

<style scoped lang="scss">
.search-group {
  &.is-focused {
    border-color: #7B61FF66;
    outline: 0;
    border-radius: 4px;
    box-shadow: 0 0 0 0.25rem #7B61FF66;
  }

  &.is-disabled {
    opacity: 0.65;
    pointer-events: none;
  }
}

.form-control:focus{
  box-shadow: none!important;
}

.search-container {
  position: relative;
  flex: 1;
}

.search-input {
  padding-right: 30px;
  border-radius: 4px 0 0 4px;
  height: 38px;
}

.clear-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--bs-gray-800);
  }
}

.search-button {
  border-radius: 0 4px 4px 0;
  height: 38px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-top: none;
  list-style-type: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;

  li {
    padding: 10px;
    cursor: pointer;
    color: var(--bs-primary);

    &:hover {
      background-color: var(--bs-gray-100);
    }
  }
}

.results-for-search {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  margin-top: 50px;

  .search-results-text {
    font-weight: 700;
    margin-bottom: 0;
    flex: 1;
    padding-right: 15px;
    word-wrap: break-word;
    -webkit-hyphens: auto;
    hyphens: auto;
  }

  .clear-search-link {
    color: var(--bs-primary);
    text-decoration: underline;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    align-self: flex-start;
  }
}
</style>